<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  startDate: { type: String, default: null },
  endDate: { type: String, default: null },
  fromTime: { type: String, default: null },
  recurrenceType: { type: String, default: 'daily' },
  selectedDays: { type: Array, default: () => [] },
  dayNames: { type: Object, default: () => ({}) },
  currentDate: { type: String, required: true },
  v$: { type: Object, required: true },
});

const emit = defineEmits([
  'update:startDate',
  'update:endDate',
  'update:fromTime',
  'update:recurrenceType',
  'update:selectedDays',
]);

const { t } = useI18n();

const recurrenceOptions = [
  { value: 'daily', label: 'Diariamente' },
  { value: 'weekly', label: 'Semanalmente (mesmo dia da semana)' },
  { value: 'monthly', label: 'Mensalmente (mesmo dia do mês)' },
  { value: 'custom', label: 'Personalizado' },
];

const formErrors = computed(() => ({
  startDate: props.v$.startDate.$error
    ? t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULING.DATE_BEGIN_ERROR')
    : '',
  endDate: props.v$.endDate.$error
    ? t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULING.DATE_END_ERROR')
    : '',
  fromTime: props.v$.fromTime.$error
    ? t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULING.FROM_TIME_ERROR')
    : '',
  selectedDays: props.v$.selectedDays.$error
    ? t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULING.SELECTED_DAYS_ERROR')
    : '',
}));

const toggleDay = (e, dayIndex) => {
  const index = Number(dayIndex);
  if (e.target.checked) {
    if (!props.selectedDays.includes(index)) {
      emit('update:selectedDays', [...props.selectedDays, index]);
    }
  } else {
    emit(
      'update:selectedDays',
      props.selectedDays.filter(d => d !== index)
    );
  }
};
</script>

<template>
  <div class="space-y-5 flex-1 px-6 mt-6">
    <!-- Date & Time Section -->
    <div class="space-y-4">
      <h3
        class="text-sm font-medium text-n-slate-12 dark:text-white flex items-center gap-2"
      >
        <span
          class="w-5 h-5 bg-n-blue-9 rounded-full flex items-center justify-center"
        >
          <svg
            class="w-2.5 h-2.5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </span>
        {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.FORM.DATE_TIME_TITLE') }}
      </h3>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div class="space-y-2">
          <label
            class="block text-sm font-medium text-n-slate-11 dark:text-white"
          >
            {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.FORM.START_DATE') }}
          </label>
          <Input
            :model-value="startDate"
            type="date"
            :min="currentDate"
            :message="formErrors.startDate"
            :message-type="formErrors.startDate ? 'error' : 'info'"
            class="w-full"
            @update:model-value="emit('update:startDate', $event)"
          />
        </div>

        <div class="space-y-2">
          <label
            class="block text-sm font-medium text-n-slate-11 dark:text-white"
          >
            {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.FORM.END_DATE') }}
          </label>
          <Input
            :model-value="endDate"
            type="date"
            :min="startDate || currentDate"
            :message="formErrors.endDate"
            :message-type="formErrors.endDate ? 'error' : 'info'"
            class="w-full"
            @update:model-value="emit('update:endDate', $event)"
          />
        </div>

        <div class="space-y-2">
          <label
            class="block text-sm font-medium text-n-slate-11 dark:text-white"
          >
            {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.FORM.TIME') }}
          </label>
          <Input
            :model-value="fromTime"
            type="time"
            :message="formErrors.fromTime"
            :message-type="formErrors.fromTime ? 'error' : 'info'"
            class="w-full"
            @update:model-value="emit('update:fromTime', $event)"
          />
        </div>
      </div>
    </div>

    <!-- Recurrence Section -->
    <div class="space-y-4">
      <h3
        class="text-sm font-medium text-n-slate-12 dark:text-white flex items-center gap-2"
      >
        <span
          class="w-5 h-5 bg-n-iris-9 rounded-full flex items-center justify-center"
        >
          <svg
            class="w-2.5 h-2.5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </span>
        {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.FORM.RECURRENCE_TITLE') }}
      </h3>

      <div class="max-w-md">
        <ComboBox
          :model-value="recurrenceType"
          class="outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 disabled:outline-n-weak dark:disabled:outline-n-weak focus:outline-n-brand-ink dark:focus:outline-n-brand-ink block w-full reset-base text-sm outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
          :options="recurrenceOptions"
          :placeholder="
            t(
              'CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.FORM.RECURRENCE_PLACEHOLDER'
            )
          "
          @update:model-value="emit('update:recurrenceType', $event)"
        />
      </div>

      <!-- Custom Days Selection -->
      <div v-if="recurrenceType === 'custom'" class="space-y-4">
        <h4 class="font-medium text-n-slate-12 dark:text-white">
          {{
            t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.FORM.CUSTOM_DAYS_TITLE')
          }}
        </h4>
        <div class="grid grid-cols-7 gap-2">
          <div
            v-for="(dayName, dayIndex) in dayNames"
            :key="dayIndex"
            class="text-center"
          >
            <input
              :id="'day-' + dayIndex"
              type="checkbox"
              :checked="selectedDays.includes(Number(dayIndex))"
              class="sr-only"
              @change="e => toggleDay(e, dayIndex)"
            />
            <label
              :for="'day-' + dayIndex"
              class="block w-10 h-10 rounded-xl border-2 cursor-pointer transition-colors flex items-center justify-center text-sm font-medium"
              :class="[
                selectedDays.includes(Number(dayIndex))
                  ? 'bg-n-blue-9 border-n-blue-9 text-white'
                  : 'bg-n-slate-1 dark:bg-n-slate-11 border-n-slate-4 dark:border-n-slate-8 text-n-slate-11 dark:text-n-slate-3 hover:border-n-blue-6',
              ]"
            >
              {{ dayName.charAt(0) }}
            </label>
            <span class="block mt-1 text-xs text-n-slate-11 dark:text-white">
              {{ dayName.slice(0, 3) }}
            </span>
          </div>
        </div>
        <span
          v-if="formErrors.selectedDays || v$.selectedDays.$error"
          class="block text-sm text-n-ruby-9"
        >
          {{ formErrors.selectedDays || v$.selectedDays.$errors[0].$message }}
        </span>
      </div>
    </div>
  </div>
</template>
